import React, { useState, useEffect, ReactNode } from 'react';
import { Transition } from 'react-transition-group';

type ExpandableSectionProps = {
    collapsedContent: ReactNode;
    expandedContent: ReactNode;
    duration?: number;
    onExpand?: () => void,
    defaultState?: boolean;
}

const ExpandableSection = ({
    collapsedContent,
    expandedContent,
    duration = 300,
    onExpand,
    defaultState = false
}: ExpandableSectionProps) => {
    const [isExpanded, setIsExpanded] = useState(defaultState);
    const [overflow, setOverflow] = useState<'hidden' | 'auto'>('hidden');
    const [maxHeight, setMaxHeight] = useState<string>('0px');

    const defaultStyle = {
        transition: `max-height ${duration}ms ease-in-out, opacity ${duration}ms ease-in-out`,
        opacity: 0,
        overflow: overflow as 'hidden' | 'auto',
    };

    const transitionStyles = {
        entering: { maxHeight: maxHeight, opacity: 1 },
        entered:  { maxHeight: maxHeight, opacity: 1 },
        exiting:  { maxHeight: '0px', opacity: 0 },
        exited:  { maxHeight: '0px', opacity: 0 },
        unmounted: { maxHeight: '0px', opacity: 0 },
    };

    useEffect(() => {
        setIsExpanded(defaultState);
    }, [defaultState]);

    useEffect(() => {
        if (isExpanded) {
            const contentHeight = document.getElementById('expandable-content')?.scrollHeight;
            setMaxHeight(`${contentHeight}px`);
            const timer = setTimeout(() => {
                setOverflow('auto');
                setMaxHeight('50vh'); // Allow content to expand freely
            }, duration);
            return () => clearTimeout(timer);
        } else {
            setOverflow('hidden');
            setMaxHeight('0px');
        }
    }, [isExpanded, duration]);

    const toggleExpansion = () => {
        setIsExpanded(!isExpanded);
        onExpand && onExpand();
    }

    return (
        <div className="w-full cursor-pointer">
            <div onClick={toggleExpansion}>
                { collapsedContent }
            </div>
            <Transition in={isExpanded} timeout={duration}>
                {state => (
                    <div style={{
                        ...defaultStyle,
                        ...transitionStyles[state],
                        overflow,
                    }}>
                        {expandedContent}
                    </div>
                )}
            </Transition>
        </div>
    );
};

export default ExpandableSection;
